<template>
  <div class="p-tabs-wrapper">
    <p-selection-parent v-model="selectedTabIndex" class="p-tabs" mandatory>
      <p-selection-child
        v-for="(tab, i) in tabs"
        v-slot="{active, toggle}"
        :key="i"
      >
        <button
          role="tab"
          :aria-selected="active ? 'true' : undefined"
          :aria-controls="'tab' + tab._uid"
          :class="getClassList(tab, active)"
          @click="clickOnTab(i, tab, toggle)"
          @keyup="onKeyUp(tab._uid)"
          @blur="onBlur(tab._uid)"
        >
          <div class="p-tab-label">
            {{ renderLabel(tab) }}
          </div>
        </button>
      </p-selection-child>
      <div class="p-tabs-border"></div>
    </p-selection-parent>
    <p-parent namespace="p-tab-content" class="p-tabs-pane" :children.sync="tabs">
      <slot></slot>
    </p-parent>
  </div>
</template>

<script lang="ts">
  import sizeable from '../../mixins/sizeable/sizeable';
  import mixins from '../../mixins';
  import PTab from './p-tab.vue';
  import PChild from '../p-parent/p-child.vue';

  type ChildType = InstanceType<typeof PChild>;
  type TabType = InstanceType<typeof PTab>;

  export default mixins(sizeable).extend({
    props: {
      value: { default: undefined, type: [Number, String] },
      color: { default: 'interaction', type: String },
      colorActive: { default: 'positive', type: String },
    },
    watch: {
      value: {
        immediate: true,
        handler() {
          if (this.value !== undefined && this.value !== null) {
            this.$set(this, 'selectedTabIndex', this.value);
          }
          this.selectedTabIndex = this.selectedTabIndex ?? 0;
        },
      },
      selectedTabIndex: {
        immediate: true,
        handler() {
          // Wait a tick, dependent on value being set first etc
          this.$nextTick(() => {
            const selectedIndex = this.selectedTabIndex;
            if (!Array.isArray(this.tabs)) {
              // If no child p-tabs inside slot
              return;
            }
            this.tabs.forEach((tab: ChildType) => {
              if ((tab?.$parent as any)?.internalActive) {
                this.$set(tab.$parent, 'internalActive', false);
                tab?.$el?.classList.remove('p-tab-active');
              }
            });
            if (selectedIndex !== undefined) {
              const tab = this.tabs[selectedIndex];
              tab?.$el?.classList.add('p-tab-active');
              this.$emit('input', selectedIndex);
              if (tab) {
                this.$set(tab?.$parent, 'internalActive', true);
              }
            }
          });
        },
      },
    },
    methods: {
      onKeyUp(tabId: any) {
        this.$set(this.navigatedUsingKeyboard, tabId, true);
      },
      onBlur(tabId: any) {
        this.$set(this.navigatedUsingKeyboard, tabId, false);
      },
      getClassList(tab: ChildType, active: boolean): any {
        const disabled = (tab.$parent as TabType).$props.disabled as boolean;
        return {
          'p-tab': true,
          // eslint-disable-next-line no-underscore-dangle
          'keyboard-focus': this.navigatedUsingKeyboard[(tab as any)._uid],
          active,
          [`color-${this.color}`]: !active && !disabled,
          [`color-${this.colorActive}`]: active && !disabled,
          'color-disabled': disabled,
          disabled,
          dense: this.dense,
          spacious: this.spacious,
        };
      },
      clickOnTab(index: number, tab: ChildType, toggle: ()=>{}) {
        if (this.getTabDisabled(tab)) {
          return;
        }
        this.$emit('click', index);
        toggle();
      },
      getTabDisabled(tab: ChildType) {
        if (!tab.$parent) {
          return true;
        }
        if (!tab.$parent.$props) {
          return true;
        }
        return (tab.$parent as TabType).$props.disabled;
      },
      renderLabel(tab: TabType) {
        if (!tab.$parent) {
          return 'Tab undefined';
        }
        if (!tab.$parent.$props) {
          return 'Tab props undefined';
        }
        return tab.$parent.$props.label;
      },
    },
    data: () => ({
      tabs: [] as ChildType[],
      navigatedUsingKeyboard: {} as Record<string | number, boolean>,
      selectedTabIndex: undefined as number | undefined,
    }),
  });
</script>
